import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useMemo, useState } from 'react';
import { LinkIcon, SearchIcon } from '@primer/octicons-react';
import { LTInputBox, LTText, LTTooltip } from '@lambdatestincprivate/lt-components';
import { openIssueDrawer, usePackState } from '../../data/pack-store';
import { StatusIcon } from '../icons/StatusIcon';
import { relativeTime, truncateMiddle } from '../format';
import { IssuesTabIcon } from '../Tabs/RunTabIcons';
import { LoadingJourney } from '../states/LoadingJourney';
import statesStyles from '../states/states.module.css';
import { IssueTypeChip } from './IssueTypeChip';
import { issueMatches } from './search';
import styles from './issues.module.css';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var LINK_ISSUE_SOON = 'Coming soon — link issues to Jira & more ✨';

/** 4px meta dot, the TMS Ellipse separator. */
var MetaDot = function MetaDot() {
  return /*#__PURE__*/_jsx("span", {
    className: styles.metaDot,
    "aria-hidden": "true"
  });
};
function IssueRow(_ref) {
  var _item$triagedAt, _item$status;
  var item = _ref.item,
    isLast = _ref.isLast,
    testTitle = _ref.testTitle;
  var triagedAgo = relativeTime((_item$triagedAt = item.triagedAt) !== null && _item$triagedAt !== void 0 ? _item$triagedAt : null);
  return /*#__PURE__*/_jsx("li", {
    className: "".concat(styles.row, " ").concat(isLast ? styles.rowLast : ''),
    children: /*#__PURE__*/_jsxs("button", {
      type: "button",
      className: styles.rowButton,
      onClick: function onClick() {
        return openIssueDrawer(item);
      },
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.rowStatus,
        children: /*#__PURE__*/_jsx(StatusIcon, {
          status: (_item$status = item.status) !== null && _item$status !== void 0 ? _item$status : 'failed',
          size: 16
        })
      }), /*#__PURE__*/_jsxs("span", {
        className: styles.rowBody,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.rowTitle,
          children: item.title
        }), /*#__PURE__*/_jsxs("span", {
          className: styles.metaLine,
          children: [/*#__PURE__*/_jsx(LTTooltip, {
            text: "Test instance: ".concat(testTitle),
            direction: "bottom",
            customTriggerStyle: {
              display: 'inline-flex',
              minWidth: 0
            },
            children: /*#__PURE__*/_jsx("span", {
              className: styles.metaBold,
              children: truncateMiddle(testTitle, 40)
            })
          }), item.ordinal !== undefined && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(MetaDot, {}), /*#__PURE__*/_jsxs("span", {
              className: styles.metaItem,
              children: ["Step ", item.ordinal]
            })]
          }), /*#__PURE__*/_jsx(MetaDot, {}), /*#__PURE__*/_jsx("span", {
            className: styles.metaItem,
            children: item.triageStatus === 'triaged' ? 'Triaged by KaneAI' : 'Untriaged'
          }), triagedAgo && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(MetaDot, {}), /*#__PURE__*/_jsx("span", {
              className: styles.metaItem,
              children: triagedAgo
            })]
          })]
        })]
      }), /*#__PURE__*/_jsx("span", {
        className: styles.rowChip,
        children: /*#__PURE__*/_jsx(IssueTypeChip, {
          category: item.category
        })
      })]
    })
  });
}

/**
 * The Issues tab, rendered entirely from the pack's failure.yaml layers
 * (Figma 30979-18328: TMS-style rows + issue-type chips). Search by name
 * only — no filter dropdowns (user decision). Clicking a row opens the
 * pack-fed RCA drawer.
 */
export function IssuesTab() {
  var attention = usePackState(function (s) {
    return s.attention;
  });
  var attentionPhase = usePackState(function (s) {
    return s.attentionPhase;
  });
  var rows = usePackState(function (s) {
    return s.rows;
  });
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];
  var items = useMemo(function () {
    var _attention$items;
    return (_attention$items = attention === null || attention === void 0 ? void 0 : attention.items) !== null && _attention$items !== void 0 ? _attention$items : [];
  }, [attention]);
  var filtered = useMemo(function () {
    return items.filter(function (i) {
      return issueMatches(i, query);
    });
  }, [items, query]);

  // The index read hasn't settled — show a loader, not a false "no
  // issues" empty state (QA: reload on the Issues tab flashed empty).
  if (items.length === 0 && attentionPhase === 'loading') {
    // No statesStyles.wrap here — it centers in the FULL pane, which
    // would land the bar lower than the opening stage's spot.
    return /*#__PURE__*/_jsx(LoadingJourney, {
      stage: "Reading failure analysis\u2026",
      variant: "pane"
    });
  }
  if (items.length === 0) {
    return /*#__PURE__*/_jsx("div", {
      className: statesStyles.wrap,
      children: /*#__PURE__*/_jsxs("div", {
        className: statesStyles.emptyTab,
        children: [/*#__PURE__*/_jsx(IssuesTabIcon, {}), /*#__PURE__*/_jsx(LTText, {
          text: "No issues in this evidence pack",
          variant: LTText.TextVariant.SUBHEADER_BOLD
        }), /*#__PURE__*/_jsx(LTText, {
          text: "This run recorded no failures \u2014 when a pack carries triaged failures, they show up here with their root-cause analysis.",
          variant: LTText.TextVariant.SUBHEADER_REGULAR,
          color: "var(--lt-text-secondary)"
        })]
      })
    });
  }
  var searching = query.trim() !== '';
  return /*#__PURE__*/_jsxs("div", {
    className: styles.page,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.header,
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.headerText,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.headerTitle,
          children: "Issues"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.headerSub,
          children: "All issues found in this evidence pack."
        })]
      }), /*#__PURE__*/_jsx(LTTooltip, {
        text: LINK_ISSUE_SOON,
        direction: "bottom",
        children: /*#__PURE__*/_jsxs("button", {
          type: "button",
          className: styles.linkIssueBtn,
          disabled: true,
          children: [/*#__PURE__*/_jsx(LinkIcon, {
            size: 16,
            "aria-hidden": "true"
          }), "Link issue"]
        })
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: styles.searchRow,
      children: /*#__PURE__*/_jsx(LTInputBox, {
        inputOnly: true,
        size: "medium",
        placeholder: "Search issue",
        leadingIcon: SearchIcon,
        value: query
        // This lib version calls onChange(value, event) — value first
        // (the event-shaped handler crashed on the main-line lib bump).
        ,
        onChange: function onChange(value) {
          return setQuery(value);
        }
        // The lib's clear button only resets its internal text — it
        // never fires onChange; sync our state through it.
        ,
        trailingAction: function trailingAction() {
          return setQuery('');
        },
        "aria-label": "Search issues by name"
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.card,
      children: [/*#__PURE__*/_jsx("div", {
        className: styles.cardHeader,
        children: /*#__PURE__*/_jsx(LTText, {
          text: "".concat(filtered.length, " issue").concat(filtered.length === 1 ? '' : 's'),
          variant: LTText.TextVariant.SUBHEADER_BOLD
        })
      }), filtered.length === 0 ? /*#__PURE__*/_jsx("div", {
        className: styles.noMatch,
        children: /*#__PURE__*/_jsx(LTText, {
          text: "No issues match \u201C".concat(query.trim(), "\u201D"),
          variant: LTText.TextVariant.SUBHEADER_REGULAR,
          color: "var(--lt-text-secondary)"
        })
      }) : /*#__PURE__*/_jsx("ul", {
        className: styles.list,
        children: filtered.map(function (item, i) {
          var _item$ordinal, _rows$get$title, _rows$get;
          return /*#__PURE__*/_jsx(IssueRow, {
            item: item,
            isLast: i === filtered.length - 1,
            testTitle: (_rows$get$title = (_rows$get = rows.get(item.testId)) === null || _rows$get === void 0 ? void 0 : _rows$get.title) !== null && _rows$get$title !== void 0 ? _rows$get$title : item.testId
          }, "".concat(item.testId, "-").concat((_item$ordinal = item.ordinal) !== null && _item$ordinal !== void 0 ? _item$ordinal : i));
        })
      })]
    }), /*#__PURE__*/_jsx("span", {
      className: "ev-sr-only",
      "aria-live": "polite",
      children: searching ? "".concat(filtered.length, " of ").concat(items.length, " issues match") : "".concat(items.length, " issues")
    })]
  });
}